import { useId, useRef } from "react";

import { Badge } from "../../../../core/Badge.js";
import { Button } from "../../../../core/Button.js";
import { cardStyles } from "../../../../core/Card.styles.js";
import { ConfirmDialog } from "../../../../core/ConfirmDialog.js";
import { EmptyState } from "../../../../core/EmptyState.js";
import { Skeleton } from "../../../../core/Skeleton.js";
import { Text } from "../../../../core/Text.js";
import { Amount } from "../../../../domain/Amount.js";
import { BoxArt } from "../../../../domain/BoxArt.js";
import { AppLink } from "../../../../foundations/AppLink.js";
import { cn } from "../../../../foundations/cn.js";
import { FOCUS_RING } from "../../../../foundations/interaction.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import { LoadError } from "../../../shared/LoadError.js";
import type {
  ForgeConfirmContent,
  ForgeMineBoxesContent,
  ForgeMineRow,
} from "../../forgeContent.js";

function Row({
  row,
  currency,
}: {
  row: ForgeMineRow;
  currency: ForgeMineBoxesContent["currency"];
}) {
  return (
    <li
      className={cn(
        cardStyles({ radius: "2xl" }),
        "grid grid-cols-[4rem_minmax(0,1fr)] items-center gap-3 p-3 sm:grid-cols-[4rem_minmax(0,1fr)_auto] md:gap-4 md:p-4",
        // A box that isn't live steps back by its art alone; its words keep their contrast.
        !row.status.live && "[&_img]:opacity-60",
      )}
    >
      <span className="block size-16 rounded-lg bg-bg-inset p-1.5">
        <BoxArt src={row.image} alt="" />
      </span>
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex min-w-0 items-center gap-2">
          {row.href ? (
            <AppLink
              href={row.href}
              className={cn(
                "truncate rounded-xs font-body text-heading-h3 text-text-primary hover:underline",
                FOCUS_RING,
              )}
            >
              {row.name}
            </AppLink>
          ) : (
            <Text as="span" variant="heading-h3" tone="primary" className="truncate">
              {row.name}
            </Text>
          )}
          <Badge tone={row.status.live ? "success" : "neutral"}>{row.status.label}</Badge>
        </div>
        <dl className="flex flex-wrap gap-x-4 gap-y-1 font-body text-body-b3 text-text-tertiary">
          {row.facts.map((fact) => (
            <div key={fact.label} className="flex items-baseline gap-1">
              <dt>{fact.label}</dt>
              <dd className="text-text-secondary">
                {"cents" in fact ? (
                  <Amount cents={fact.cents} currency={currency} size="meta" />
                ) : (
                  fact.text
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="col-span-2 flex flex-wrap justify-end gap-1.5 sm:col-span-1">
        {row.actions.map((action) =>
          action.href !== undefined ? (
            <ActionButton key={action.label} action={action} variant="secondary" size="sm" />
          ) : (
            <Button
              key={action.label}
              variant="ghost"
              size="sm"
              disabled={action.disabled}
              onClick={action.onClick}
              className={action.tone === "danger" ? "text-status-danger" : undefined}
            >
              {action.label}
            </Button>
          ),
        )}
      </div>
    </li>
  );
}

/**
 * The creator's boxes as rows (13 · Exchange, Checkout's order rows): art, name and status, what
 * each has done, and Edit, Archive or Unarchive, Delete. Archive and Delete ask first.
 */
export function ForgeMineBoxes({ title, currency, state, confirm }: ForgeMineBoxesContent) {
  const id = useId();
  // The dialog keeps its words while it closes.
  const last = useRef<ForgeConfirmContent | null>(null);
  if (confirm) last.current = confirm;
  const shown = confirm ?? last.current;
  return (
    <section
      aria-labelledby={id}
      aria-busy={state.status === "loading" || undefined}
      className="flex flex-col gap-4"
    >
      <Text as="h2" id={id} variant="display-xl4" tone="primary">
        {title}
      </Text>
      {state.status === "loading" && (
        <div className="flex flex-col gap-3">
          {["a", "b", "c"].map((key) => (
            <Skeleton key={key} className="h-24 rounded-2xl" />
          ))}
        </div>
      )}
      {state.status === "error" && <LoadError message={state.message} />}
      {state.status === "empty" && (
        <EmptyState
          title={state.message}
          action={state.action && <ActionButton action={state.action} variant="secondary" />}
        />
      )}
      {state.status === "ready" && (
        <ul className="flex flex-col gap-3">
          {state.rows.map((row) => (
            <Row key={row.id} row={row} currency={currency} />
          ))}
        </ul>
      )}
      {shown && (
        <ConfirmDialog
          open={confirm !== null}
          onOpenChange={(open) => {
            if (!open) shown.onCancel();
          }}
          title={shown.title}
          description={shown.description}
          confirmLabel={shown.confirmLabel}
          cancelLabel={shown.cancelLabel}
          tone={shown.tone}
          onConfirm={shown.onConfirm}
        />
      )}
    </section>
  );
}
